/**
 * LocalImpactLayer — near-field (~50 km) view of plant-level impacts.
 *
 * Default tab. Plant markers sized by `spatial_breakdown.local[metric]`
 * rather than displaced GWh, so the visual weight matches the scale
 * we're talking about ("most acute near the plant").
 *
 * Phase 3 polish (Apr 2026):
 *   - Project marker is a distinct star (not a diamond) with
 *     a "no local benefits here" label to visually separate project
 *     vs. displaced plants.
 *   - Tooltip "Top impact categories" is now filtered by the active
 *     metric (DALYs / PDFs / USD) so all rows share a unit.
 *   - Tooltip typography bumped to `text-sm` body with a white
 *     card background for legibility.
 *   - Rough exposed-population estimate for the 50 km ring.
 */

import { useMemo } from "react";
import { CircleMarker, Circle, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import type { DisplacedGeneratorEnriched } from "../../model/domain/types";
import {
  MetricKey,
  METRIC_LABEL,
  approxLocalPopulation,
  fmtGwh,
  fmtMetric,
  fmtMw,
  fmtPopulation,
  getFuelColor,
  getFuelDisplayName,
  pickMetric,
  stateName,
  topImpactCategories,
} from "../helpers";

// ──────────────────────────────────────────────────────────────────────
// Icons — shared with other layers
// ──────────────────────────────────────────────────────────────────────

/**
 * Project marker: cyan-filled star with a white outline so it reads
 * as a "source" visually distinct from the circular plant markers
 * (which are the actual impact locations).
 */
export const projectIcon = L.divIcon({
  className: "",
  html: `<div style="
    width: 22px; height: 22px;
    display: flex; align-items: center; justify-content: center;
    filter: drop-shadow(0 0 4px rgba(0,229,255,0.5));
  ">
    <svg viewBox="0 0 24 24" width="22" height="22"
         xmlns="http://www.w3.org/2000/svg"
         style="display:block">
      <polygon points="12,1 15,9 23,9 16.5,14 19,22 12,17 5,22 7.5,14 1,9 9,9"
        fill="#00e5ff" stroke="#ffffff" stroke-width="1.2" stroke-linejoin="round" />
    </svg>
  </div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// ──────────────────────────────────────────────────────────────────────
// Marker sizing — log scale on the chosen local-scale metric
// ──────────────────────────────────────────────────────────────────────

function radiusForValue(value: number, max: number): number {
  if (max <= 0 || value <= 0) return 6;
  // Smooth sqrt scaling from ~6 to 22 px
  const t = Math.sqrt(value / max);
  return Math.max(6, Math.min(22, 6 + t * 16));
}

// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────

interface Props {
  plants: DisplacedGeneratorEnriched[];
  metric: MetricKey;
  projectLatitude?: number;
  projectLongitude?: number;
  /** Whether to draw the 50 km indicative rings. */
  showRings?: boolean;
  /** Called when the user hovers a plant (used by BasinAttributionLayer). */
  onPlantHover?: (plantId: string | number | null) => void;
}

export function LocalImpactLayer({
  plants,
  metric,
  projectLatitude,
  projectLongitude,
  showRings = true,
  onPlantHover,
}: Props) {
  const { mappable, maxLocal } = useMemo(() => {
    const m = plants.filter((p) => p.latitude && p.longitude);
    const max = m.reduce(
      (acc, p) => Math.max(acc, pickMetric(p, "local", metric)),
      0,
    );
    return { mappable: m, maxLocal: max };
  }, [plants, metric]);

  return (
    <>
      {/* Optional 50 km indicative rings (drawn first so markers sit on top) */}
      {showRings &&
        mappable.map((p) => (
          <Circle
            key={`ring-${p.plant_id}`}
            center={[p.latitude, p.longitude]}
            radius={50_000}
            pathOptions={{
              color: getFuelColor(p.fuel_type),
              weight: 0.8,
              opacity: 0.35,
              fillOpacity: 0.04,
              dashArray: "4 4",
            }}
            interactive={false}
          />
        ))}

      {/* Plant markers */}
      {mappable.map((p) => {
        const color = getFuelColor(p.fuel_type);
        const localValue = pickMetric(p, "local", metric);
        const radius = radiusForValue(localValue, maxLocal);
        // Fix: filter categories by the active metric so units align.
        const topCats = topImpactCategories(p.impacts_by_category, 4, metric);
        const localPop = approxLocalPopulation(p.state);

        return (
          <CircleMarker
            key={p.plant_id}
            center={[p.latitude, p.longitude]}
            radius={radius}
            pathOptions={{
              fillColor: color,
              color: "#ffffff",
              weight: 1,
              fillOpacity: 0.8,
              opacity: 0.7,
            }}
            eventHandlers={
              onPlantHover
                ? {
                    mouseover: () => onPlantHover(p.plant_id),
                    mouseout: () => onPlantHover(null),
                  }
                : undefined
            }
          >
            <Popup className="spatial-popup">
              <div className="text-sm min-w-[240px] text-gray-800">
                <p className="font-semibold text-gray-900 text-[15px] mb-1">
                  {p.plant_name}
                </p>
                <p className="text-gray-600 text-xs mb-0.5">
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1"
                    style={{ backgroundColor: color }}
                  />
                  {getFuelDisplayName(p.fuel_type)}
                  {" \u2022 "}
                  {fmtMw(p.capacity_mw)} MW
                </p>
                <p className="text-gray-600 text-xs mb-1">
                  {p.county}, {stateName(p.state)}
                </p>
                <p className="text-gray-800 font-medium">
                  {fmtGwh(Math.abs(p.displaced_gwh))} GWh displaced
                </p>

                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                    Local-scale contribution ({METRIC_LABEL[metric]})
                  </div>
                  <div className="text-gray-900 font-semibold">
                    {fmtMetric(localValue, metric)} {METRIC_LABEL[metric]}
                  </div>
                  {localPop > 0 && (
                    <div className="text-[11px] text-gray-600 mt-0.5">
                      50 km ring reaches {fmtPopulation(localPop)} (indicative)
                    </div>
                  )}
                </div>

                {topCats.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                      Top impact categories ({METRIC_LABEL[metric]})
                    </div>
                    <ul className="space-y-0.5">
                      {topCats.map((c) => (
                        <li key={c.key} className="flex justify-between gap-3 text-gray-700">
                          <span>{c.label}</span>
                          <span className="text-gray-900 tabular-nums font-medium">
                            {fmtMetric(c.value, metric)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="mt-2 pt-2 border-t border-gray-200 text-[11px] text-gray-500 leading-tight">
                  Local impacts land <strong>here</strong> — at the displaced
                  plant — not at your project site.
                </p>
              </div>
            </Popup>
          </CircleMarker>
        );
      })}

      {/* Project location marker — star, visually distinct from plant dots */}
      {projectLatitude != null &&
        projectLongitude != null &&
        projectLatitude !== 0 &&
        projectLongitude !== 0 && (
          <Marker
            position={[projectLatitude, projectLongitude]}
            icon={projectIcon}
            zIndexOffset={1000}
          >
            <Popup className="spatial-popup">
              <div className="text-sm min-w-[220px] text-gray-800">
                <p className="font-semibold text-gray-900 text-[15px]">
                  Your project site
                </p>
                <p className="text-gray-600 text-xs mt-1 leading-snug">
                  <strong>No local benefits here.</strong> Your project
                  generates electrons that displace the coal/gas plants shown
                  as circles — and the local air-quality benefits land around
                  those plants, not here.
                </p>
              </div>
            </Popup>
          </Marker>
        )}
    </>
  );
}
